import { useEffect } from "react";

import { useQueries } from "@tanstack/react-query";

import type {
    ConversionRatesCategoriesOverviewPopListData,
    ConversionRatesCategoriesOverviewPopListParams,
    ConversionRatesCategoryOverviewPopListData,
} from "@similarweb/pro-api-client";
import Sentry from "@similarweb/sentry-logger";

import { getQueryOptions, useApiQuery } from "app/modules/api-client";
import type { ICategoryComparison } from "app/pages/industry-analysis/categories-context/CategoriesContext";

type SharedParams = Omit<ConversionRatesCategoriesOverviewPopListParams, "category">;

/**
 * The endpoint returns an empty body when a category has no conversion data, so we normalize
 * incomplete payloads to undefined, mirroring the single-category hook.
 */
const normalizeCategoryData = (
    data: Partial<ConversionRatesCategoryOverviewPopListData> | undefined,
): ConversionRatesCategoryOverviewPopListData | undefined =>
    data && data.totalConvertedVisits && data.avgConversionRate
        ? (data as ConversionRatesCategoryOverviewPopListData)
        : undefined;

/**
 * Same rationale as the non-PoP variant: the combined "categories overview" endpoint doesn't support
 * segment-scoped custom industries, so when any compared category has segments we fall back to N
 * parallel single-category requests and merge them into the combined endpoint's response shape.
 * Categories without data are simply omitted, so the table still renders for whichever categories
 * do have data.
 */
const buildFallbackEntries = (
    categories: ICategoryComparison[],
    results: Array<{ data?: ConversionRatesCategoryOverviewPopListData }>,
): ConversionRatesCategoriesOverviewPopListData => {
    return categories.reduce<ConversionRatesCategoriesOverviewPopListData>((entries, { category }, index) => {
        const data = normalizeCategoryData(results[index]?.data);
        if (!data) {
            return entries;
        }

        entries.push({
            category: category.forApi,
            convertedVisits: data.totalConvertedVisits,
            avgConversionRate: data.avgConversionRate,
            // unused by getComparisonOverviewPopColumns, which recomputes its own share from convertedVisits
            normalizedConvertedVisits: 0,
        });
        return entries;
    }, []);
};

export const useConversionRateComparisonOverviewPopData = (
    categories: ICategoryComparison[],
    sharedParams: SharedParams,
    { enabled, hasSelectedCategoryWithSegments }: { enabled: boolean; hasSelectedCategoryWithSegments: boolean },
) => {
    const combinedParams = {
        ...sharedParams,
        category: categories.map(({ category }) => category.forApi),
    } satisfies ConversionRatesCategoriesOverviewPopListParams;

    const combinedQuery = useApiQuery(
        {
            tag: "WebConversionRates",
            methodName: "conversionRatesCategoriesOverviewPopList",
            options: { enabled: enabled && !hasSelectedCategoryWithSegments, placeholderData: undefined },
        },
        combinedParams,
    );

    const fallbackQueries = useQueries({
        queries: categories.map(({ category }) =>
            getQueryOptions(
                {
                    tag: "WebConversionRates",
                    methodName: "conversionRatesCategoryOverviewPopList",
                    options: { enabled: enabled && hasSelectedCategoryWithSegments, placeholderData: undefined },
                },
                { ...sharedParams, category: category.forApi },
            ),
        ),
        combine: (results) => ({
            data: buildFallbackEntries(categories, results),
            isLoading: results.some((result) => result.isLoading),
            isError: results.some((result) => result.isError),
            error: results.find((result) => result.error)?.error ?? null,
        }),
    });

    const { data, isLoading, isError, error } = hasSelectedCategoryWithSegments
        ? fallbackQueries
        : {
              data: combinedQuery.data,
              isLoading: combinedQuery.isLoading,
              isError: combinedQuery.isError,
              error: combinedQuery.error,
          };

    useEffect(() => {
        if (isError) {
            Sentry.errorTracker({
                e: error instanceof Error ? error : new Error("Conversion rate categories PoP overview request failed"),
                message: "Failed to load conversion rate categories PoP overview",
                level: "error",
            });
        }
    }, [isError, error]);

    return { data, isLoading, isError };
};
